"use client";

import React, { useMemo, useRef, useEffect } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { sanitizeForMarkdown, decodeEntities } from "@/lib/sanitize";

import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

/**
 * Mermaid diagram renderer. Uses the `mermaid` npm package to turn
 * ```mermaid fenced blocks into inline SVG diagrams. Falls back to
 * a plain code block if rendering fails.
 */
let _mermaid: any = null;
async function getMermaid(): Promise<any> {
  if (_mermaid) return _mermaid;
  try {
    const m = await import("mermaid");
    _mermaid = m.default || m;
    _mermaid.initialize({ startOnLoad: false, securityLevel: "strict", fontFamily: "inherit" });
    return _mermaid;
  } catch {
    return null;
  }
}

function MermaidBlock({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = React.useState<string | null>(null);
  const [error, setError] = React.useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const id = `mermaid-${Math.random().toString(36).slice(2, 11)}`;
    let cancelled = false;
    (async () => {
      const mermaid = await getMermaid();
      if (!mermaid || !cancelled) {
        if (!mermaid) { setError(true); return; }
      }
      if (!mermaid || cancelled) return;
      try {
        const { svg: out } = await mermaid.render(id, code.trim(), containerRef.current);
        if (!cancelled) setSvg(out);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (error || !svg) {
    return (
      <div className="my-4 overflow-x-auto">
        <pre className="text-xs bg-muted/40 border border-border/50 rounded-lg p-3 whitespace-pre-wrap">
          {code.trim()}
        </pre>
      </div>
    );
  }
  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: svg }} className="my-4 overflow-x-auto" />;
}

/**
 * Normalise LaTeX delimiters emitted by the LLM so remark-math can parse them.
 * LLMs frequently produce \[ ... \] (display) and \( ... \) (inline) which
 * remark-math does NOT understand — it expects $$...$$ and $...$. Without this
 * conversion the raw delimiters leak into the rendered answer.
 *
 * sanitizeForMarkdown strips script/iframe/handler markup but deliberately
 * leaves `< > " ' :` intact, so relational operators (x < 5, a > b) and quotes
 * render unescaped. decodeEntities fixes already-mangled stored content.
 */
function normaliseMath(input: string): string {
  if (!input) return "";
  let out = decodeEntities(sanitizeForMarkdown(input));

  // Some transports/store round-trips preserve JSON-style newline escapes as
  // literal backslash-n sequences. Convert those to real newlines so
  // whitespace-pre-wrap + markdown paragraph breaks behave correctly.
  out = out.replace(/\\n/g, "\n");

  // Some transports (JSON round-trips, stored content) double-escape the
  // backslash, producing `\\[` instead of `\[`. Normalise those first so the
  // rules below can match consistently.
  out = out
    .replace(/\\\\\[/g, "\\[")
    .replace(/\\\\\]/g, "\\]")
    .replace(/\\\\\(/g, "\\(")
    .replace(/\\\\\)/g, "\\)");
  // JSON-escaped LaTeX commands: `\\cos` -> `\cos`, `\\sin` -> `\sin`, etc.
  out = out.replace(/\\\\([a-zA-Z]+)/g, "\\$1");

  // Display math: \[ ... \] -> $$ ... $$ (handle multiline)
  out = out.replace(/\\\[([\s\S]*?)\\\]/g, (m, inner) => `$$${inner}$$`);
  // Inline math: \( ... \) -> $ ... $
  out = out.replace(/\\\(([\s\S]*?)\\\)/g, (m, inner) => `$${inner}$`);

  // LaTeX sometimes arrives wrapped in a single pair of quotes/braces; ensure
  // the delimiters themselves are not quoted so remark-math can parse them.
  out = out.replace(/[`'"](\$\$?[\s\S]*?\$\$?)[`'"]/g, "$1");

  // Strip outer braces wrapping math delimiters
  out = out.replace(
    /\{\s*(\$\$?)([\s\S]*?)(\1)\s*\}/g,
    (m, open, inner, close) => `${open}${inner}${close}`,
  );

  return out;
}

interface MathMarkdownProps {
  children: string;
  className?: string;
}

/**
 * Renders markdown text with LaTeX math support (both $...$/$$...$$ and the
 * \(...\) / \[...\] delimiter styles) and GitHub Flavored Markdown (tables, lists).
 */
export function MathMarkdown({ children, className = "" }: MathMarkdownProps) {
  const content = useMemo(() => normaliseMath(children || ""), [children]);
  const remarkPlugins = useMemo(() => [remarkMath, remarkGfm], []);
  // rehypeRaw: allow trusted raw HTML in markdown (tables, formatted text the LLM emits as HTML)
  // rehypeKatex: LaTeX math rendering (throwOnError:false => malformed formula renders in red, not crash)
  const rehypePlugins = useMemo(
    () =>
      [
        rehypeRaw,
        [
          rehypeKatex,
          { throwOnError: false, strict: false, errorColor: "#cc0000" },
        ],
      ] as any,
  [],);
  // External links in answers/notes open in a new browsing context so the
  // user never navigates away from the app.
  const markdownComponents: Components = {
    a: ({ node, ...props }) => {
      const href = props.href;
      const isExternal = typeof href === "string" && /^https?:\/\//i.test(href);
      return (
        <a
          {...props}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="text-primary hover:underline font-medium break-words"
        />
      );
    },
    table: ({ node, ...props }) => (
      <div className="w-full my-4 overflow-x-auto rounded-xl border border-border/60 shadow-sm">
        <table
          {...props}
          className="w-full text-sm text-left border-collapse"
        />
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead
        {...props}
        className="bg-muted/70 text-foreground font-semibold border-b border-border/80"
      />
    ),
    tbody: ({ node, ...props }) => (
      <tbody {...props} className="divide-y divide-border/40" />
    ),
    tr: ({ node, ...props }) => (
      <tr {...props} className="hover:bg-muted/30 transition-colors" />
    ),
    th: ({ node, ...props }) => (
      <th
        {...props}
        className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground"
      />
    ),
    td: ({ node, ...props }) => (
      <td
        {...props}
        className="px-4 py-3 text-sm text-foreground/90 leading-normal"
      />
    ),
    p: ({ node, ...props }) => (
      <p {...props} className="whitespace-pre-wrap" />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote
        {...props}
        className="border-l-4 border-primary/60 pl-4 py-1.5 my-3 italic text-muted-foreground bg-muted/20 rounded-r-lg"
      />
    ),
    code: ({
      node,
      className: codeClassName,
      children: codeChildren,
      ...props
    }: any) => {
      const isInline =
        !codeClassName &&
        typeof codeChildren === "string" &&
        !codeChildren.includes("\n");

      // Mermaid fenced block: ```mermaid … ```
      if (codeClassName === "language-mermaid" || codeClassName === "mermaid") {
        return <MermaidBlock code={typeof codeChildren === "string" ? codeChildren : ""} />;
      }

      if (isInline) {
        return (
          <code
            {...props}
            className="px-1.5 py-0.5 rounded bg-muted/80 text-foreground font-mono text-[0.875em] border border-border/50"
          >
            {codeChildren}
          </code>
        );
      }
      return (
        <code {...props} className={codeClassName}>
          {codeChildren}
        </code>
      );
    },
  };

  return (
    <div
      className={`math-markdown leading-relaxed overflow-x-auto max-w-full ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default MathMarkdown;
